import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { nativeSelectProps, shrinkLabelProps } from './ScheduleFormSection';
import {
  DEFAULT_RULES,
  GAME_TYPE_OPTIONS,
  MAX_QUICK_ADD_GAMES,
  buildPlayoffRoundGames,
  buildSemiFinalAndFinalGames,
} from './scheduleFormModel';

const PRESET_PLAYOFF_ROUND = 'playoff-round';
const PRESET_SEMIS_AND_FINAL = 'semis-final';
const MAX_GAP_MINUTES = 720;

const QUICK_ADD_GAME_TYPES = GAME_TYPE_OPTIONS.filter((o) => o.value !== 'Bye');

const toWholeNumber = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const number = Number(value);
  return Number.isInteger(number) ? number : null;
};

const initialState = (defaultGapMinutes) => ({
  preset: PRESET_PLAYOFF_ROUND,
  gameType: 'Playoff',
  date: '',
  firstTime: '19:00',
  count: '4',
  semiDate: '',
  semiTime: '19:00',
  finalDate: '',
  finalTime: '20:30',
  venueId: '',
  fieldIds: [],
  gapMinutes: String(toWholeNumber(defaultGapMinutes) ?? DEFAULT_RULES.gameLengthMinutes),
});

const validate = (state) => {
  if (state.preset === PRESET_PLAYOFF_ROUND) {
    if (!state.date) return 'Choose a date.';
    if (!state.firstTime) return 'Choose the first kick-off time.';
    const count = toWholeNumber(state.count);
    if (count === null || count < 1 || count > MAX_QUICK_ADD_GAMES) return `Number of games must be between 1 and ${MAX_QUICK_ADD_GAMES}.`;
  } else {
    if (!state.semiDate || !state.semiTime) return 'Choose the semi-final date and kick-off time.';
    if (!state.finalDate || !state.finalTime) return 'Choose the final date and kick-off time.';
    if (state.finalDate < state.semiDate) return 'The final must be on or after the semi-final date.';
  }
  if (!state.venueId) return 'Choose a venue.';
  const gap = toWholeNumber(state.gapMinutes);
  if (gap === null || gap < 1 || gap > MAX_GAP_MINUTES) return `Minutes between kick-offs must be between 1 and ${MAX_GAP_MINUTES}.`;
  return null;
};

const buildGames = (state) => {
  const gapMinutes = toWholeNumber(state.gapMinutes);
  if (state.preset === PRESET_PLAYOFF_ROUND) {
    return buildPlayoffRoundGames({
      gameType: state.gameType,
      date: state.date,
      venueId: state.venueId,
      fieldIds: state.fieldIds,
      firstTime: state.firstTime,
      count: toWholeNumber(state.count),
      gapMinutes,
    });
  }
  return buildSemiFinalAndFinalGames({
    semiDate: state.semiDate,
    semiTime: state.semiTime,
    finalDate: state.finalDate,
    finalTime: state.finalTime,
    venueId: state.venueId,
    fieldIds: state.fieldIds,
    gapMinutes,
  });
};

const QuickAddGamesDialog = ({ open, onClose, onAdd, venues = [], defaultGapMinutes }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [state, setState] = useState(() => initialState(defaultGapMinutes));
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setState(initialState(defaultGapMinutes));
      setError(null);
    }
  }, [open, defaultGapMinutes]);

  const venue = useMemo(
    () => venues.find((v) => String(v.id) === String(state.venueId)) || null,
    [venues, state.venueId],
  );
  // Keep fields in the venue's order so games fill them predictably.
  const selectedFieldIds = (venue?.fields || [])
    .map((f) => String(f.id))
    .filter((id) => state.fieldIds.includes(id));

  const update = (patch) => {
    setError(null);
    setState((prev) => ({ ...prev, ...patch }));
  };

  const toggleField = (fieldId) => {
    const id = String(fieldId);
    update({
      fieldIds: state.fieldIds.includes(id)
        ? state.fieldIds.filter((f) => f !== id)
        : [...state.fieldIds, id],
    });
  };

  const handleAdd = () => {
    const normalized = { ...state, fieldIds: selectedFieldIds };
    const validationError = validate(normalized);
    if (validationError) {
      setError(validationError);
      return;
    }
    onAdd(buildGames(normalized));
  };

  const isPlayoffRound = state.preset === PRESET_PLAYOFF_ROUND;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" fullScreen={isMobile} aria-labelledby="quick-add-games-title">
      <DialogTitle id="quick-add-games-title">Quick add games</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <ToggleButtonGroup
            value={state.preset}
            exclusive
            onChange={(_, value) => value && update({ preset: value })}
            aria-label="Quick add preset"
            fullWidth
            size="small"
            orientation={isMobile ? 'vertical' : 'horizontal'}
          >
            <ToggleButton value={PRESET_PLAYOFF_ROUND}>Playoff round</ToggleButton>
            <ToggleButton value={PRESET_SEMIS_AND_FINAL}>Semi-finals + final</ToggleButton>
          </ToggleButtonGroup>

          <Typography variant="body2" color="text.secondary">
            {isPlayoffRound
              ? 'Games fill the selected fields at each kick-off time, then move to the next kick-off. Teams are left as TBD.'
              : 'Adds 1st vs 4th and 2nd vs 3rd semi-finals, then a final between the winners. Teams are left as TBD.'}
          </Typography>

          {isPlayoffRound ? (
            <>
              <TextField
                label="Game type"
                select
                value={state.gameType}
                onChange={(e) => update({ gameType: e.target.value })}
                fullWidth
                slotProps={nativeSelectProps}
              >
                {QUICK_ADD_GAME_TYPES.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </TextField>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Date"
                  type="date"
                  value={state.date}
                  onChange={(e) => update({ date: e.target.value })}
                  required
                  fullWidth
                  slotProps={shrinkLabelProps}
                />
                <TextField
                  label="First kick-off"
                  type="time"
                  value={state.firstTime}
                  onChange={(e) => update({ firstTime: e.target.value })}
                  required
                  fullWidth
                  slotProps={{ ...shrinkLabelProps, htmlInput: { step: 300 } }}
                />
              </Stack>
              <TextField
                label="Number of games"
                type="number"
                value={state.count}
                onChange={(e) => update({ count: e.target.value })}
                required
                fullWidth
                slotProps={{ ...shrinkLabelProps, htmlInput: { min: 1, max: MAX_QUICK_ADD_GAMES } }}
              />
            </>
          ) : (
            <>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Semi-final date"
                  type="date"
                  value={state.semiDate}
                  onChange={(e) => update({ semiDate: e.target.value })}
                  required
                  fullWidth
                  slotProps={shrinkLabelProps}
                />
                <TextField
                  label="Semi-final kick-off"
                  type="time"
                  value={state.semiTime}
                  onChange={(e) => update({ semiTime: e.target.value })}
                  required
                  fullWidth
                  slotProps={{ ...shrinkLabelProps, htmlInput: { step: 300 } }}
                />
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Final date"
                  type="date"
                  value={state.finalDate}
                  onChange={(e) => update({ finalDate: e.target.value })}
                  required
                  fullWidth
                  slotProps={shrinkLabelProps}
                />
                <TextField
                  label="Final kick-off"
                  type="time"
                  value={state.finalTime}
                  onChange={(e) => update({ finalTime: e.target.value })}
                  required
                  fullWidth
                  slotProps={{ ...shrinkLabelProps, htmlInput: { step: 300 } }}
                />
              </Stack>
            </>
          )}

          <TextField
            label="Venue"
            select
            value={state.venueId}
            onChange={(e) => update({ venueId: e.target.value, fieldIds: [] })}
            required
            fullWidth
            slotProps={nativeSelectProps}
          >
            <option value="">Choose a venue…</option>
            {venues.map((v) => (
              <option key={v.id} value={v.id}>{v.name}</option>
            ))}
          </TextField>

          {venue && (
            <FormControl component="fieldset">
              <FormLabel component="legend">Fields</FormLabel>
              {(venue.fields || []).length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  This venue has no fields. Each game gets its own kick-off time.
                </Typography>
              ) : (
                <FormGroup row>
                  {venue.fields.map((f) => (
                    <FormControlLabel
                      key={f.id}
                      control={(
                        <Checkbox
                          checked={state.fieldIds.includes(String(f.id))}
                          onChange={() => toggleField(f.id)}
                        />
                      )}
                      label={f.name}
                      sx={{ wordBreak: 'break-word' }}
                    />
                  ))}
                </FormGroup>
              )}
            </FormControl>
          )}

          <TextField
            label="Minutes between kick-offs"
            type="number"
            value={state.gapMinutes}
            onChange={(e) => update({ gapMinutes: e.target.value })}
            required
            fullWidth
            helperText={isPlayoffRound
              ? 'Used when there are more games than selected fields'
              : 'Used when fewer than two fields are selected, so the second semi-final follows the first'}
            slotProps={{ ...shrinkLabelProps, htmlInput: { min: 1, max: MAX_GAP_MINUTES } }}
          />

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleAdd}>Add games</Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuickAddGamesDialog;
